import { createElement } from 'react';

import {
    type DynamicWidgetAdapter,
    type DynamicWidgetBuildContext,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_registry';
import {
    buildDynamicWidgetId,
    dynamicWidgetEventSourceSchema,
    type DynamicWidgetIssue,
    DynamicWidgetModelBuilder,
    getDynamicWidgetSourceId,
    type ShiftListWidgetModelV1,
    shiftListWidgetModelV1Schema,
    type ShiftListWidgetRow,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_schema';
import {
    addOptionalSourceField,
    buildDayGroups,
    buildShiftRow,
    cardBindingPresentation,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/legacy_shift_list_model';
import {
    collectShiftOccurrenceIds,
    getActionableShiftRows,
    isActionableShiftStatus,
    legacyShiftOutputSchema,
    nullableShiftStringSchema,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/shift_list_model';
import { ShiftListWidget } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/shift_list_widget';
import { type ToolExecutionEntry } from '@apps/web/components/phoebe_chat_view/transcript_types';

export { shiftListValidationFields } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/legacy_shift_list_model';

export function buildShiftListWidgetModel(
    entry: ToolExecutionEntry,
    context: DynamicWidgetBuildContext,
): ShiftListWidgetModelV1 | null {
    if (
        entry.name !== 'shifts_search' ||
        entry.callId === null ||
        !entry.toolCallEventId
    ) {
        return null;
    }

    const output = legacyShiftOutputSchema.parse(entry.output);
    const source = dynamicWidgetEventSourceSchema.parse({
        kind: 'event_source',
        toolName: entry.name,
        callId: entry.callId,
        toolCallEventId: entry.toolCallEventId,
        toolOutputEventId: entry.id,
        phase: 'output',
        capturedAt: entry.createdAt,
        organizationId: context.organizationId,
        mode: context.mode,
    });
    const sourceId = getDynamicWidgetSourceId(source);
    const builder = new DynamicWidgetModelBuilder([source]);
    const issues: Array<DynamicWidgetIssue> = [];
    const rows: Array<ShiftListWidgetRow> = [];
    const sourceRowIndexes: Array<number> = [];
    output.shifts.forEach((rawRow, sourceRowIndex) => {
        const row = buildShiftRow({
            rawRow,
            rowIndex: rows.length,
            sourceRowIndex,
            builder,
            issues,
            sourceId,
        });
        if (row !== null) {
            rows.push(row);
            sourceRowIndexes.push(sourceRowIndex);
        }
    });
    const dayGroups = buildDayGroups(rows, sourceRowIndexes, builder, sourceId);

    const rawNote =
        typeof output['message'] !== 'undefined'
            ? output['message']
            : output['hint'];
    const notePath =
        typeof output['message'] !== 'undefined' ? '/message' : '/hint';
    const note = addOptionalSourceField({
        builder,
        issues,
        sourceId,
        id: 'note',
        path: notePath,
        rawValue: rawNote,
        schema: nullableShiftStringSchema,
        fallback: null,
        renderedValue: (value) => value,
        presentation: cardBindingPresentation('note'),
    });
    const statusPaths = sourceRowIndexes.map(
        (sourceRowIndex) => `/shifts/${sourceRowIndex}/status`,
    );
    const openRows = rows.filter((row) => isActionableShiftStatus(row.status));
    const actionableOpenRows = getActionableShiftRows(rows);
    const collectedBulkShiftIds = collectShiftOccurrenceIds(actionableOpenRows);
    const rowCount = builder.addCalculatedField({
        id: 'rowCount',
        sourceIds: [sourceId],
        paths: ['/shifts'],
        transform: 'countValidShiftRows',
        rawValue: output.shifts,
        value: rows.length,
        renderedValue: String(rows.length),
        presentation: cardBindingPresentation('rowCount'),
    });
    const openShiftCount = builder.addCalculatedField({
        id: 'openShiftCount',
        sourceIds: [sourceId],
        paths: statusPaths,
        transform: 'countOpenShiftRows',
        rawValue: rows.map((row) => row.status),
        value: openRows.length,
        renderedValue: String(openRows.length),
        presentation: cardBindingPresentation('openShiftCount'),
    });
    const bulkOpenShiftCount = builder.addCalculatedField({
        id: 'bulkOpenShiftCount',
        sourceIds: [sourceId],
        paths: [
            ...statusPaths,
            ...sourceRowIndexes.map(
                (sourceRowIndex) => `/shifts/${sourceRowIndex}/shift_id`,
            ),
        ],
        transform: 'countActionableOpenShiftRows',
        rawValue: rows.map((row) => ({
            occurrenceIds: row.occurrenceIds,
            status: row.status,
        })),
        value: actionableOpenRows.length,
        renderedValue: String(actionableOpenRows.length),
        presentation: cardBindingPresentation('bulkOpenShiftCount'),
    });
    const bulkShiftIds = builder.addCalculatedField({
        id: 'bulkShiftIds',
        sourceIds: [sourceId],
        paths: actionableOpenRows.flatMap((row) => {
            const sourceRowIndex = sourceRowIndexes[rows.indexOf(row)];
            return [
                `/shifts/${sourceRowIndex}/shift_id`,
                `/shifts/${sourceRowIndex}/future_occurrences`,
            ];
        }),
        transform: 'collectActionableOccurrenceIds',
        rawValue: actionableOpenRows.map((row) => row.occurrenceIds),
        value: collectedBulkShiftIds,
        renderedValue:
            collectedBulkShiftIds.length === 0
                ? null
                : JSON.stringify(collectedBulkShiftIds),
        presentation: cardBindingPresentation('bulkShiftIds', 'control'),
    });

    return shiftListWidgetModelV1Schema.parse({
        ...builder.buildBase({
            widgetId: buildDynamicWidgetId(entry.toolCallEventId, 'shifts'),
            templateVersion: 1,
            issues,
        }),
        template: 'shifts',
        templateVersion: 1,
        rows,
        dayGroups,
        note,
        rowCount,
        openShiftCount,
        bulkOpenShiftCount,
        bulkShiftIds,
    });
}

export const shiftListAdapter: DynamicWidgetAdapter<ShiftListWidgetModelV1> = {
    template: 'shifts',
    templateVersion: 1,
    toolNames: new Set(['shifts_search']),
    build: buildShiftListWidgetModel,
    render(model, handlers) {
        return createElement(ShiftListWidget, {
            model,
            onCaregiverClick: handlers.onCaregiverClick,
        });
    },
};
